import React, { useMemo } from 'react';
import { ExtraTableRenderSettings } from '../../components/ExtraFields';
import { Trans, useTranslation } from 'react-i18next';
import { useCurrencyFormatter } from '../../../../hooks/useCurrencyFormatter';
import FormattedDate from '../../../../components/date/FormattedDate';
import { Expense, ExpenseStatusInfo } from './types';
import { ExpenseStatusCell } from './Common/ExpenseStatusCell';
import {
  ExpenseStatus,
  ExtraFieldsTable_ExtraFieldValueFragment,
} from '../../__generated__/graphql';
import { EmployeeWithAvatar } from '../../components/Employee';
import SoftBadge from '../../components/SoftBadge';
import { mapExpenseStatus } from './mappings';
import CommentCount from '../../components/Comment/CommentCount/CommentCount';
import ItemsCount from '../../components/ItemsCount/ItemsCount';
import { ExtraFieldsTableCustomColumns } from '../../components/ExtraFields/types';
import FlairIcon from '../../../../atomic/atoms/FlairIcon';
import './useExpenseRenderSettings.css';

export type Item = {
  id: string;
  showComments?: boolean;
  showItems?: boolean;
};

type Props = {
  onItemClick: (item: Item) => void;
  onDeleteExpense?: (expenseId: string) => void;
  isMyExpenses?: boolean;
};

const EMPTY_VALUE = '---';

export const useExpenseTableRenderSettings = ({
  onItemClick,
  onDeleteExpense,
  isMyExpenses,
}: Props) => {
  const { t } = useTranslation();
  const formatCurrency = useCurrencyFormatter();

  const columns: ExtraTableRenderSettings<Expense>['columns'] = useMemo(() => {
    return {
      Name: {
        Header: <Trans t={t} i18nKey="expenses.table.header.name" />,
        Cell: ({ row, value }) => {
          return (
            <div title={value} className="text-truncate expense-name-container">
              <a
                href="stub"
                onClick={(e) => {
                  e.preventDefault();
                  onItemClick({ id: row.original.id });
                }}>
                {value}
              </a>
            </div>
          );
        },
      },
      flair__Merchant__c: {
        Header: <Trans t={t} i18nKey="expenses.table.header.merchant" />,
      },
      flair__Project__c: {
        Header: <Trans t={t} i18nKey="expenses.table.header.project" />,
      },
      flair__Expense_Category__c: {
        Header: <Trans t={t} i18nKey="expenses.table.header.category" />,
        Cell: ({ value }) => {
          return <div>{value ? value : EMPTY_VALUE}</div>;
        },
      },
      flair__Final_Amount__c: {
        Header: <Trans t={t} i18nKey="expenses.table.header.finalAmount" />,
        Cell: ({ row, value }) => {
          return (
            <div>
              {formatCurrency({
                value: value,
                currency: row.original.currencyIsoCode ?? '',
              })}
            </div>
          );
        },
      },
      flair__Expense_Date__c: {
        Header: <Trans t={t} i18nKey="expenses.table.header.expenseDate" />,
        Cell: ({ value }) => {
          return <FormattedDate day={value} format="short" />;
        },
      },
      flair__Status__c: {
        Header: <Trans t={t} i18nKey="expenses.table.header.status" />,
        Cell: ({ row }) => {
          const readonly = true;
          const id = row.original.id;
          const statusInfo: ExpenseStatusInfo = {
            expenseId: id,
            status: mapExpenseStatus({
              flair__Status__c: row.original.flair__Status__c,
              flair__Reimbursed__c: row.original.flair__Reimbursed__c,
            }),
            declineReason: null,
          };
          return (
            <ExpenseStatusCell
              value={statusInfo}
              readonly={readonly}
              updatingToStatus={null}
            />
          );
        },
      },
      flair__Submitted_By__c: {
        Header: <Trans t={t} i18nKey="expenses.table.header.employee" />,
        Cell: ({ value, row }) => {
          const submittedBy: ExtraFieldsTable_ExtraFieldValueFragment | null =
            row.original.rawExtraFields.flair__Submitted_By__c;
          const colleagueReference = submittedBy?.referenceTo;
          if (
            !colleagueReference ||
            colleagueReference.__typename !== 'Colleague'
          ) {
            return null;
          }
          return (
            <div className="d-flex align-items-center gap-2">
              <a
                href="stub"
                onClick={(e) => {
                  e.preventDefault();
                  onItemClick({ id: row.original.id });
                }}>
                <EmployeeWithAvatar
                  employee={{
                    firstName: colleagueReference.flair__First_Name__c ?? '',
                    lastName: colleagueReference.flair__Last_Name__c ?? '',
                    avatarUrl: colleagueReference.avatar?.url,
                  }}
                />
              </a>
              {row.original.isNew && (
                <SoftBadge variant="primary">
                  {t('expenses.table.newLabel')}
                </SoftBadge>
              )}
            </div>
          );
        },
      },
    };
  }, [formatCurrency, onItemClick, t]);

  const afterPersistentColumns: ExtraTableRenderSettings<Expense>['afterPersistentColumns'] =
    useMemo(() => {
      return {
        [ExtraFieldsTableCustomColumns.expenses.amount]: {
          Header: <Trans t={t} i18nKey="expenses.table.header.finalAmount" />,
          Cell: ({ row }) => {
            return (
              <div>
                {formatCurrency({
                  value: row.original.finalAmount ?? 0,
                  currency: row.original.currencyIsoCode ?? '',
                })}
              </div>
            );
          },
        },
        [ExtraFieldsTableCustomColumns.expenses.items]: {
          Header: <Trans t={t} i18nKey="expenses.table.header.items" />,
          Cell: ({ row }) => {
            const itemsCount = row.original.expenseItems?.length ?? 0;
            return (
              <ItemsCount
                count={itemsCount}
                onClick={
                  itemsCount > 0
                    ? () =>
                        onItemClick({ id: row.original.id, showItems: true })
                    : undefined
                }
              />
            );
          },
        },
        [ExtraFieldsTableCustomColumns.expenses.comments]: {
          Header: <Trans t={t} i18nKey="expenses.table.header.comments" />,
          Cell: ({ row }) => {
            return (
              <CommentCount
                count={row.original.commentsCount ?? -1}
                onClick={() =>
                  onItemClick({ id: row.original.id, showComments: true })
                }
              />
            );
          },
        },
        ...(onDeleteExpense && isMyExpenses
          ? {
              [ExtraFieldsTableCustomColumns.expenses.deleteAction]: {
                Header: '',
                Cell: ({ row }) => {
                  const expense = row.original;
                  const isDraft =
                    expense.flair__Status__c === ExpenseStatus.Draft;

                  if (!isDraft) {
                    return null;
                  }

                  return (
                    <button
                      type="button"
                      className="btn btn-sm expense-delete-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteExpense(expense.id);
                      }}
                      title={t('expenses.table.deleteExpense')}>
                      <FlairIcon icon="trash-outline" size="sm" />
                    </button>
                  );
                },
              },
            }
          : {}),
      };
    }, [onItemClick, formatCurrency, onDeleteExpense, isMyExpenses, t]);

  return { columns, afterPersistentColumns };
};
